import React, { useRef, useState, useEffect } from 'react';
import { Draggable } from "@hello-pangea/dnd";
import PillDropdown from '../../../../components/PillDropdown';
import userIcon from '../../../../imgs/user.png';

// TaskItem component to handle individual task editing with hooks
export default function TaskItem({ task, index, storyTitle, isEditing, owner, ownerName, users, stories, onUpdateTaskField, onCreateStoryAndAssign, onSetEditingTaskId, onSetEditingTaskField, hasEstimateInList, hasStatusInList, hasAssigneeInList, hasStoryInList, isSelected, isHighlighted }) {
  const taskRef = useRef(null);
  const assigneeRef = useRef(null);
  const titleInputRef = useRef(null);
  const estimateRef = useRef(null);
  const statusRef = useRef(null);
  const storyRef = useRef(null);
  const [showAllFields, setShowAllFields] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [titleWidth, setTitleWidth] = useState(100);
  const [storyInputValue, setStoryInputValue] = useState("");
  const [storyEditingMode, setStoryEditingMode] = useState(false); // Track if story field is in editing mode (cleared selection)
  
  // Reset story editing mode when task changes or editing ends
  useEffect(() => {
    if (!isEditing && !showAllFields) {
      setStoryEditingMode(false);
      setStoryInputValue("");
    }
  }, [isEditing, showAllFields, task.id]);
  
  // Helper function to focus a field by ref
  const focusField = (fieldRef) => {
    if (!fieldRef || !fieldRef.current) return;
    
    // Use requestAnimationFrame to ensure the field is ready
    requestAnimationFrame(() => {
      // For PillDropdown, use the getRef method or focus method
      if (fieldRef.current) {
        if (fieldRef.current.getRef) {
          const input = fieldRef.current.getRef();
          if (input) {
            input.focus();
          }
        } else if (fieldRef.current.focus) {
          fieldRef.current.focus();
        } else if (fieldRef.current.tagName === 'INPUT') {
          fieldRef.current.focus();
        }
      }
    });
  };
  
  // Handle Tab navigation between fields
  const handleTabNavigation = (e, currentField) => {
    if (e.key !== 'Tab' || e.shiftKey) return; // Only handle forward Tab
    
    // Only handle Tab when editing existing tasks
    if (!(isEditing || showAllFields)) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // Determine which fields are visible
    const fieldsVisible = {
      estimate: hasEstimateInList || showAllFields,
      status: hasStatusInList || showAllFields,
      assignee: hasAssigneeInList || showAllFields,
      story: hasStoryInList || showAllFields || isEditing,
    };
    
    // Define field order
    const fieldOrder = ['title', 'estimate', 'status', 'assignee', 'story'];
    
    // Find current field index
    const currentIndex = fieldOrder.indexOf(currentField);
    if (currentIndex === -1) return;
    
    // Special case: if we're at story, always wrap back to title
    if (currentField === 'story') {
      if (titleInputRef.current) {
        titleInputRef.current.focus();
      }
      return;
    }
    
    // Find next visible field
    let nextIndex = currentIndex + 1;
    while (nextIndex < fieldOrder.length) {
      const nextField = fieldOrder[nextIndex];
      
      // Check if field is visible
      if (nextField === 'title' || fieldsVisible[nextField]) {
        // Focus the next field
        if (nextField === 'title') {
          if (titleInputRef.current) {
            titleInputRef.current.focus();
          }
        } else if (nextField === 'estimate' && estimateRef.current) {
          focusField(estimateRef);
        } else if (nextField === 'status' && statusRef.current) {
          focusField(statusRef);
        } else if (nextField === 'assignee' && assigneeRef.current) {
          focusField(assigneeRef);
        } else if (nextField === 'story' && storyRef.current) {
          focusField(storyRef);
        }
        return;
      }
      nextIndex++;
    }
    
    // If we've reached the end without finding a visible field, wrap back to title
    if (titleInputRef.current) {
      titleInputRef.current.focus();
    }
  };
  
  // Listen for reset event to reset showAllFields
  useEffect(() => {
    const handleReset = () => {
      setShowAllFields(false);
    };
    document.addEventListener('resetTaskItems', handleReset);
    return () => {
      document.removeEventListener('resetTaskItems', handleReset);
    };
  }, []);
  
  // Function to get text width using canvas
  const getTextWidth = (text) => {
    if (!text && text !== '') return 0;
    if (!titleInputRef.current) return 0;
    
    const computedStyle = window.getComputedStyle(titleInputRef.current);
    const font = `${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;
    
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = font;
    return context.measureText(text || '').width;
  };
  
  // Update width when task title changes
  useEffect(() => {
    const text = task.title || '';
    const textWidth = getTextWidth(text);
    setTitleWidth(Math.max(50, textWidth + 10));
  }, [task.title]);
  
  // Handler to end editing when Enter is pressed (only for existing tasks)
  // This will be called when Enter is pressed in a PillDropdown field
  // PillDropdown will stop propagation when selecting an item, so this only runs when no item is selected
  // When an item IS selected, the onSelect callback will handle ending editing
  const handleEndEditingOnEnter = (e) => {
    if (e.key === 'Enter' && (isEditing || showAllFields) && !e.defaultPrevented) {
      // Blur the input like Escape does
      if (e.target && e.target.tagName === 'INPUT') {
        e.target.blur();
      }
      // End editing
      onSetEditingTaskId(null);
      onSetEditingTaskField(null);
      setShowAllFields(false);
    }
  };
  
  // Simplified click-outside and focus management
  useEffect(() => {
    if (!isEditing && !showAllFields) return;
    
    const handleClickOutside = (event) => {
      const taskElement = taskRef.current;
      if (!taskElement) return;
      
      // Check if click is within the task element
      const isWithinTask = taskElement.contains(event.target);
      
      if (!isWithinTask) {
        // Hide unpopulated fields if they're showing
        if (showAllFields) {
          setShowAllFields(false);
        }
        // Exit edit mode if no input is focused
        if (isEditing) {
          // Use requestAnimationFrame to check focus after all events have processed
          requestAnimationFrame(() => {
            const activeElement = document.activeElement;
            const isInputFocused = taskElement.contains(activeElement) && 
                                 (activeElement.tagName === 'INPUT' || 
                                  activeElement.tagName === 'TEXTAREA');
            
            if (!isInputFocused) {
              onSetEditingTaskId(null);
              onSetEditingTaskField(null);
            }
          });
        }
      }
    };
    
    // Use mousedown in capture phase to catch before other handlers
    document.addEventListener('mousedown', handleClickOutside, true);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [isEditing, showAllFields, onSetEditingTaskId, onSetEditingTaskField]);
  
  return (
    <Draggable draggableId={`task-${task.id}`} index={index}>
      {(provided, snapshot) => {
        const getItemStyle = (draggableStyle, isDragging) => {
          // When dragging, the library provides position: fixed with calculated top/left
          // We must spread draggableStyle FIRST to ensure its positioning takes precedence
          // Then add our custom styles that don't interfere with positioning
          if (isDragging && draggableStyle) {
            // Compensate for scroll offset - the library calculates as if scroll is 0
            // but when the page is scrolled, the drag preview appears too high
            // We need to add the current scroll position to move it down to the correct position
            const currentScrollY = window.scrollY || window.pageYOffset || 0;
            
            // Adjust the top position to account for scroll (add scroll to move down)
            const adjustedStyle = {
              ...draggableStyle,
              top: typeof draggableStyle.top === 'number' 
                ? draggableStyle.top + currentScrollY 
                : draggableStyle.top,
            };

            return {
              ...adjustedStyle, // Library's positioning (position: fixed, top, left, etc.) - MUST come first
              background: '#d0d0d0',
              borderRadius: 4,
              padding: '0px 12px',
              minWidth: 100,
              height: 30,
              boxSizing: 'border-box',
              fontSize: 12,
              color: '#111',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'grabbing',
              marginBottom: 3,
            };
          }
          
          // When not dragging, use position: relative
          return {
            position: "relative",
            background: '#e0e0e0',
            borderRadius: 4,
            padding: '0px 12px',
            minWidth: 100,
            height: 30,
            boxSizing: 'border-box',
            fontSize: 12,
            color: '#111',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: isEditing ? 'default' : 'grab',
            marginBottom: 3,
            // Merge any additional styles from the library (should be minimal when not dragging)
            ...draggableStyle,
          };
        };
        
        return (
          <div
            ref={(node) => {
              provided.innerRef(node);
              taskRef.current = node;
            }}
            {...provided.draggableProps}
            {...(!isEditing ? provided.dragHandleProps : {})}
            style={getItemStyle(provided.draggableProps.style, snapshot.isDragging)}
            data-task-id={task.id}
            data-show-all-fields={showAllFields ? 'true' : 'false'}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={(e) => {
              // Stop propagation to prevent container click handler from firing
              e.stopPropagation();
              
              // Check if clicking on a field (input, editable-field, pen icon, or dropdown)
              const target = e.target;
              const isInput = target.tagName === 'INPUT';
              const isEditableField = target.closest('.editable-field');
              const isPenIcon = target.closest('.edit-pen-icon');
              const isDropdown = target.closest('input[type="text"]') && target.closest('div[style*="position: relative"]');
              
              // If not clicking on any field, toggle showAllFields (same as clicking the pen icon)
              if (!isInput && !isEditableField && !isPenIcon && !isDropdown) {
                setShowAllFields(!showAllFields);
              }
            }}
          >
            {(isSelected || isHighlighted) && (
              <div style={{ 
                position: "absolute", 
                left: -2, 
                top: -2, 
                right: -2, 
                bottom: -2, 
                border: "3px solid orange", 
                borderRadius: 6,
                pointerEvents: "none",
                zIndex: 1
              }} />
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
              {/* Title - always editable, looks like plain text, auto-sized */}
              <input
                ref={titleInputRef}
                type="text"
                defaultValue={task.title || ''}
                style={{
                  width: `${titleWidth}px`,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  fontSize: 12,
                  color: '#111',
                  padding: 0,
                  minWidth: 50,
                  cursor: 'text',
                }}
                onFocus={() => {
                  // Show all fields when editing title
                  setShowAllFields(true);
                }}
                onChange={(e) => {
                  const text = e.target.value || '';
                  const textWidth = getTextWidth(text);
                  setTitleWidth(Math.max(50, textWidth + 10));
                }}
                onBlur={(e) => {
                  // Use requestAnimationFrame to check after all events process
                  requestAnimationFrame(() => {
                    const activeElement = document.activeElement;
                    const isWithinTask = taskRef.current?.contains(activeElement);
                    
                    // Don't save if focus moved to another field in the task
                    if (!isWithinTask && e.target.value.trim() !== (task.title || '')) {
                      onUpdateTaskField(task.id, 'title', e.target.value.trim());
                    }
                  });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.target.blur();
                    // End editing when Enter is pressed (only for existing tasks)
                    // Check if we're editing (either isEditing is true or showAllFields is true)
                    if (isEditing || showAllFields) {
                      onSetEditingTaskId(null);
                      onSetEditingTaskField(null);
                      setShowAllFields(false);
                    }
                  } else if (e.key === 'Tab' && !e.shiftKey) {
                    handleTabNavigation(e, 'title');
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
              
              {/* Fields container - right-aligned if populated, left-aligned if not */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: (task.status || task.estimate || task.owner_id || task.story_id || showAllFields) ? 'auto' : 0, position: 'relative' }}>
                {/* Pen icon - shows on hover, absolutely positioned to the left of fields, hidden when showing all fields */}
                <span
                  className="edit-pen-icon"
                  style={{
                    position: 'absolute',
                    right: '100%',
                    marginRight: 6,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    opacity: (isHovered && !showAllFields) ? 1 : 0,
                    transition: 'opacity 0.2s',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 14,
                    width: 14,
                    zIndex: 10,
                    color: '#555',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAllFields(!showAllFields);
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" fill="currentColor" stroke="currentColor" strokeWidth="0.5"/>
                  </svg>
                </span>
                {/* Estimate - reserve space if any task has it, show component if populated or showAllFields */}
                {(hasEstimateInList || showAllFields) && (
                  <div 
                    className="editable-field"
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                    style={{ display: "flex", justifyContent: 'flex-start', marginRight: 6 }}
                  >
                    {(task.estimate || showAllFields) ? (
                      <PillDropdown
                        ref={estimateRef}
                        hasPrefixNumber={true}
                        prefixNumber={
                          task.estimate
                            ? task.estimate <= 8 || task.estimate % 8 !== 0
                              ? task.estimate
                              : task.estimate / 8
                            : null
                        }
                        list={[{ value: "days", id: "days" }, { value: "hrs", id: "hrs" }]}
                        selectedId={task.estimate ? (task.estimate > 8 && task.estimate % 8 === 0 ? "days" : "hrs") : null}
                        placeholder="Estimate"
                        onSelect={(item, numberValue) => {
                          // numberValue is passed from PillDropdown when prefixNumber mode is active
                          const number = numberValue || (task.estimate && (task.estimate <= 8 || task.estimate % 8 !== 0)
                            ? task.estimate
                            : task.estimate ? task.estimate / 8 : 0) || 0;
                          const estimate = item.id === "hrs" ? number : number * 8;
                          onUpdateTaskField(task.id, 'estimate', estimate);
                          // End editing after selection (only for existing tasks)
                          // PillDropdown already blurs the input, so we just need to end editing
                          if (isEditing || showAllFields) {
                            requestAnimationFrame(() => {
                              onSetEditingTaskId(null);
                              onSetEditingTaskField(null);
                              setShowAllFields(false);
                            });
                          }
                        }}
                        onClear={() => onUpdateTaskField(task.id, 'estimate', 'remove')}
                        onKeyDown={(e) => {
                          handleEndEditingOnEnter(e);
                          if (e.key === 'Tab' && !e.shiftKey) {
                            handleTabNavigation(e, 'estimate');
                          }
                        }}
                        pillStyle={{
                          background: "transparent",
                          color: "gray",
                          borderRadius: 4,
                          fontSize: 10,
                          padding: "1px 0px",
                          minWidth: 50,
                          textAlign: 'right',
                        }}
                      />
                    ) : (
                      <div style={{ width: 50, height: 20 }} />
                    )}
                  </div>
                )}
                
                {/* Status - reserve space if any task has it, show component if populated or showAllFields */}
                {(hasStatusInList || showAllFields) && (
                  <div 
                    className="editable-field"
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                    style={{ display: "flex", justifyContent: 'flex-end', flexShrink: 0 }}
                  >
                    {(task.status || showAllFields) ? (
                      <PillDropdown
                        ref={statusRef}
                        list={[
                          { value: 'TODO', id: 'TODO' },
                          { value: 'IN PROGRESS', id: 'IN_PROGRESS' },
                          { value: 'REVIEW', id: 'REVIEW' },
                          { value: 'DONE', id: 'DONE' },
                        ]}
                        selectedId={task.status || null}
                        placeholder="Status"
                        onSelect={(item) => {
                          onUpdateTaskField(task.id, 'status', item.id);
                          // End editing after selection (only for existing tasks)
                          // PillDropdown already blurs the input, so we just need to end editing
                          if (isEditing || showAllFields) {
                            requestAnimationFrame(() => {
                              onSetEditingTaskId(null);
                              onSetEditingTaskField(null);
                              setShowAllFields(false);
                            });
                          }
                        }}
                        onClear={() => onUpdateTaskField(task.id, 'status', 'remove')}
                        onKeyDown={(e) => {
                          handleEndEditingOnEnter(e);
                          if (e.key === 'Tab' && !e.shiftKey) {
                            handleTabNavigation(e, 'status');
                          }
                        }}
                        pillStyle={{
                          background: task.status === 'DONE' ? '#4CAF50' : 
                                     task.status === 'IN_PROGRESS' ? '#FF9800' : 
                                     task.status === 'REVIEW' ? '#2196F3' : '#9E9E9E',
                          color: 'white',
                          fontSize: 9,
                          borderRadius: 2,
                          minWidth: 0,
                          padding: "3px 2px 3px 2px",
                        }}
                      />
                    ) : (
                      <div style={{ width: 50, height: 20 }} />
                    )}
                  </div>
                )}
                
                {/* Assignee - reserve space if any task has it, show component if populated or showAllFields */}
                {(hasAssigneeInList || showAllFields) && (
                  <div style={{ position: 'relative', width: 20, height: 20, margin: '-6px 0px -6px 0px', flexShrink: 0 }}>
                    {(task.owner_id || showAllFields) ? (
                      <>
                        {owner ? (
                          <div 
                            style={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              backgroundColor: owner.avatar ? 'transparent' : '#999',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 7,
                              fontWeight: 'bold',
                              overflow: 'hidden',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              cursor: 'pointer',
                            }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              // Focus the PillDropdown input using ref
                              if (assigneeRef.current) {
                                e.preventDefault();
                                setTimeout(() => {
                                  const input = assigneeRef.current.getRef?.();
                                  if (input) {
                                    input.focus();
                                    input.click(); // Ensure input is active
                                  } else if (assigneeRef.current.focus) {
                                    assigneeRef.current.focus();
                                  }
                                }, 0);
                              }
                            }}
                          >
                            {owner.avatar ? (
                              <img 
                                src={owner.avatar} 
                                alt={`${owner.firstname} ${owner.lastname || ''}`}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.style.backgroundColor = '#999';
                                }}
                              />
                            ) : null}
                            {!owner.avatar && (
                              <span>
                                {owner.firstname ? owner.firstname.substring(0, 1).toUpperCase() + owner.firstname.substring(1, 2).toLowerCase() : ''}{owner.lastname ? owner.lastname.substring(0, 1).toUpperCase() : ''}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div 
                            style={{
                              width: 18,
                              height: 18,
                              borderRadius: '50%',
                              border: '1px solid #bbb',
                              backgroundColor: 'transparent',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              cursor: 'pointer',
                              pointerEvents: 'auto',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (assigneeRef.current) {
                                const input = assigneeRef.current.getRef?.();
                                if (input) {
                                  input.focus();
                                  input.click(); // Ensure input is active
                                } else if (assigneeRef.current.focus) {
                                  assigneeRef.current.focus();
                                }
                              }
                            }}
                          >
                            <img 
                              src={userIcon} 
                              alt="User"
                              style={{
                                width: 6,
                                height: 8,
                                opacity: 0.2,
                                pointerEvents: 'none',
                              }}
                            />
                          </div>
                        )}
                        <div 
                          className="editable-field assignee-pill"
                          style={{ 
                            position: owner ? 'absolute' : 'relative',
                            top: owner ? 0 : 'auto',
                            left: owner ? 0 : 'auto',
                            width: 20,
                            height: 20,
                          }}
                        >
                          <PillDropdown
                            ref={assigneeRef}
                            list={(users || []).map((val) => ({
                              value: !val.lastname ? val.firstname : val.firstname + " " + val.lastname.charAt(0),
                              id: val.id,
                            }))}
                            selectedId={task.owner_id || null}
                            placeholder=""
                            onSelect={(item) => {
                              onUpdateTaskField(task.id, 'assignee', item.id);
                              // End editing after selection (only for existing tasks)
                              // PillDropdown already blurs the input, so we just need to end editing
                              if (isEditing || showAllFields) {
                                requestAnimationFrame(() => {
                                  onSetEditingTaskId(null);
                                  onSetEditingTaskField(null);
                                  setShowAllFields(false);
                                });
                              }
                            }}
                            onClear={() => onUpdateTaskField(task.id, 'assignee', 'remove')}
                            onKeyDown={(e) => {
                              handleEndEditingOnEnter(e);
                              if (e.key === 'Tab' && !e.shiftKey) {
                                handleTabNavigation(e, 'assignee');
                              }
                            }}
                            pillStyle={{
                              background: 'transparent',
                              color: 'transparent',
                              border: 'none',
                              borderRadius: '50%',
                              fontSize: 0,
                              padding: 0,
                              minWidth: 20,
                              width: 20,
                              height: 20,
                            }}
                          />
                        </div>
                      </>
                    ) : (
                      <div style={{ width: 20, height: 20 }} />
                    )}
                  </div>
                )}
                
                {/* Story - reserve space if any task has it, show component if populated, showAllFields, or when editing */}
                {(hasStoryInList || showAllFields || isEditing) && (
                  <div 
                    className="editable-field story-field"
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                    style={{ position: 'relative', width: 60, height: 16 }}
                  >
                    {(task.story_id || showAllFields || isEditing) ? (
                      <PillDropdown
                        ref={storyRef}
                        list={(() => {
                          const storyList = (stories || []).filter(story => story.status !== 'COMPLETE').map((story) => ({
                            value: story.title || 'Untitled Story',
                            id: story.id,
                          }));
                          
                          // If input is empty, add "No story" option at the top
                          if (!storyInputValue.trim() && (isEditing || showAllFields)) {
                            return [
                              { value: 'No story', id: '__no_story__' },
                              ...storyList
                            ];
                          }
                          
                          // If user is typing and the text doesn't match any existing story, add "Create new:" option
                          if (storyInputValue.trim() && isEditing) {
                            const searchLower = storyInputValue.toLowerCase().trim();
                            const hasMatch = storyList.some(story => 
                              story.value.toLowerCase().includes(searchLower)
                            );
                            
                            if (!hasMatch) {
                              return [
                                { value: `Create new: "${storyInputValue.trim()}"`, id: '__create_new__' },
                                ...storyList
                              ];
                            }
                          }
                          
                          return storyList;
                        })()}
                        selectedId={storyEditingMode ? null : (task.story_id || null)}
                        value={storyEditingMode 
                          ? storyInputValue
                          : (task.story_id 
                            ? (stories?.find(s => s.id === task.story_id)?.title || 'Untitled Story')
                            : storyInputValue)}
                        placeholder="Story"
                        onChange={(e) => {
                          setStoryInputValue(e.target.value);
                          // When user starts typing, enter editing mode
                          if (!storyEditingMode && task.story_id) {
                            setStoryEditingMode(true);
                          }
                        }}
                        onSelect={async (item) => {
                          if (item.id === '__no_story__') {
                            // Clear the story assignment
                            onUpdateTaskField(task.id, 'story', 'remove');
                            setStoryInputValue("");
                            setStoryEditingMode(false);
                          } else if (item.id === '__create_new__' && onCreateStoryAndAssign) {
                            // Create new story with the typed name
                            const storyName = storyInputValue.trim();
                            if (storyName) {
                              await onCreateStoryAndAssign(task.id, storyName);
                              setStoryInputValue("");
                              setStoryEditingMode(false);
                            }
                          } else {
                            onUpdateTaskField(task.id, 'story', item.id);
                            setStoryInputValue("");
                            setStoryEditingMode(false);
                          }
                          // End editing after selection (only for existing tasks)
                          // PillDropdown already blurs the input, so we just need to end editing
                          if (isEditing || showAllFields) {
                            requestAnimationFrame(() => {
                              onSetEditingTaskId(null);
                              onSetEditingTaskField(null);
                              setShowAllFields(false);
                            });
                          }
                        }}
                        onClear={() => {
                          onUpdateTaskField(task.id, 'story', 'remove');
                          setStoryInputValue("");
                          setStoryEditingMode(false);
                        }}
                        onKeyDown={(e) => {
                          // Handle backspace when story is selected - clear selection and enter editing mode
                          if (e.key === 'Backspace' && task.story_id && !storyEditingMode && !storyInputValue) {
                            e.preventDefault();
                            e.stopPropagation();
                            setStoryEditingMode(true);
                            setStoryInputValue("");
                            // Focus the input to allow typing
                            if (storyRef.current?.getRef) {
                              const input = storyRef.current.getRef();
                              if (input) {
                                input.focus();
                                // Clear the input value so user can start typing
                                if (input) {
                                  input.value = "";
                                }
                              }
                            }
                            return;
                          }
                          
                          // Handle Enter when input is empty - clear the story
                          // BUT only if there's no match in the dropdown (let PillDropdown handle selection first)
                          if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
                            // If there's any input text, let PillDropdown handle the Enter key first
                            // PillDropdown will select the highlighted item if there is one
                            if (storyInputValue.trim()) {
                              // Don't interfere - let PillDropdown handle the selection
                              // PillDropdown calls customOnKeyDown AFTER handling Enter, so if we get here,
                              // it means PillDropdown didn't handle it (no highlighted item), so we can safely ignore
                              return;
                            }
                            
                            // Only handle Enter to clear if input is empty
                            if (storyEditingMode && !storyInputValue.trim()) {
                              e.preventDefault();
                              e.stopPropagation();
                              onUpdateTaskField(task.id, 'story', 'remove');
                              setStoryInputValue("");
                              setStoryEditingMode(false);
                              // End editing
                              if (isEditing || showAllFields) {
                                requestAnimationFrame(() => {
                                  onSetEditingTaskId(null);
                                  onSetEditingTaskField(null);
                                  setShowAllFields(false);
                                });
                              }
                              return;
                            }
                          }
                          
                          handleEndEditingOnEnter(e);
                          if (e.key === 'Tab' && !e.shiftKey) {
                            handleTabNavigation(e, 'story');
                          }
                        }}
                        right
                        customStyle={{
                          width: "100%",
                        }}
                      />
                    ) : (
                      <div style={{ width: 60, height: 20 }} />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      }}
    </Draggable>
  );
}


